/**
 * The console's API surface, typed.
 *
 * Two things to know before reading it:
 *
 * 1. **A denial is a 200.** `POST /agent/authorize` returns HTTP 200 for allow, deny AND
 *    escalate — the verdict is in the body. Same for `/agent/usage/consume`, where refused is
 *    `{allowed: false}`. Treating a non-2xx as the only failure reports a blocked agent as
 *    healthy.
 * 2. **Money is a decimal string, and the ledger is integer µUSD.** Neither is ever a float:
 *    a float cannot be signed reproducibly, so a record containing one verifies in Python and
 *    fails in JS.
 *
 * The shapes here are what `CONSOLE_CONTRACT` in `test_ui_contract.py` pins. A field rendered
 * from this file that is not in that list can disappear without anything failing first.
 */

import { Client } from "@authoxi/ui-shared";
import { BFF_API_BASE } from "./session";

export interface Tenant {
  tenant_id: string;
  name: string;
  publishable_key: string;
  status: string;
}

export interface Agent {
  agent_id: string;
  agent_did: string;
  name: string | null;
  status: string; // active | revoked
  kind: string;
  principal_did: string | null;
  type_id: string | null;
  allowed_scopes: string[];
  created_at: string;
}

export interface Mandate {
  mandate_id: string;
  subject_id: string;
  subject_kind: string;
  subject_did: string | null;
  budget: string;
  spent: string;
  remaining: string;
  currency: string;
  allowed_actions: string[];
  escalate_over: string | null;
  /** `deny` · `warn` · `dryrun`. **Shadow mode is a MANDATE property, not an agent one** —
   *  an agent is `active` or `revoked`; whether its verdicts bite is decided here. */
  enforcement: string;
  status: string;
  expires_at: string | null;
}

export interface Grant {
  grant_id: string;
  agent_id: string;
  system: string;
  scopes: string[];
  status: string;
}

export interface AgentType {
  type_id: string;
  name: string;
  description: string;
  policy: Record<string, unknown>;
}

export interface UsageRow {
  agent_id: string;
  resource: string;
  used: number; // integer µUSD (or raw units for non-money resources)
  limit: number | null; // null ⇒ metered only, never blocks
  remaining: number | null;
}

export interface Pending {
  pending_id: string;
  agent_id: string;
  mandate_id: string;
  action: string;
  amount: string;
  currency: string;
  ctx: Record<string, unknown>;
  status: string;
  resolution: "approve" | "deny" | null;
  created_at?: string;
}

export interface DecisionRow {
  observation_id: string;
  agent_id: string;
  action_type: string;
  decision: string;
  reason_code: string | null;
  enforcement: string;
  amount: string | null;
  currency: string;
  sensitivity: number | null;
  created_at: string | null;
}

export interface DecisionSummary {
  window_hours: number;
  total: number;
  allow: number;
  deny: number;
  escalate: number;
  challenge: number;
  shadowed: number;
  by_reason: Record<string, number>;
  by_action: Record<string, number>;
  /** Always false, and rendered. This is the only thing separating a working view from the
   *  in-process ring that was deliberately deleted. */
  is_record: boolean;
  window_note: string;
}

export interface Member {
  member_id: string;
  email: string;
  role: string;
  status: string;
}

export interface Connection {
  provider: string;
  mode: "hosted" | "self_hosted";
  status: string;
}

export interface Detector {
  detector_id: string;
  name: string;
  detector_did: string;
  status: string;
}

export interface ShadowReport {
  window_size: number;
  enforcing: number;
  shadowed: number;
  would_have_denied: number;
  would_have_escalated: number;
  caught: number;
  by_reason: Record<string, number>;
  by_action: Record<string, number>;
  by_agent: Record<string, number>;
  money_at_risk: Record<string, string>;
  samples: { agent_id: string; action_type: string; decision: string; reason_code: string | null; amount: string; currency: string }[];
}

export interface MandateProposal {
  subject_id: string;
  allowed_actions: string[];
  currency: string;
  budget: string;
  escalate_over: string | null;
  observations_seen: number;
  would_allow: number;
  would_escalate: number;
  would_deny: number;
  coverage_ratio: number;
  notes: string[];
}

/** The published taxonomy. The CLASS arrives with the code and both are inside the signed
 *  body — a caller that picks one and invents the other signs a body the server did not
 *  build, and gets a 403 that says nothing useful. */
export interface ReasonCode {
  reason_code: string;
  reason_class: string;
}

export interface ReasonCodes {
  deny: ReasonCode[];
  approve: ReasonCode[];
}

export interface Identifier {
  identifier_id: string;
  /** `email` or `phone`. */
  type: string;
  value: string;
  is_primary: boolean;
  /** Whether the ACCOUNT ever proved this identifier — durable, and NOT the same
   *  question as how the current session was established. */
  verified: boolean;
  verified_at: string | null;
  created_at: string;
}

export interface TenantApp {
  app_id: string;
  name: string;
  slug: string;
  publishable_key: string;
  /** Apps sharing an account space share accounts. By default each is its own. */
  account_scope: string;
  /** The other apps in this scope. Empty means this app's accounts are its own. */
  shares_accounts_with: string[];
  status: string;
  /**
   * Where this app is served from — the one fact an operator actually holds, and
   * the same list that decides where its sessions and password-reset links may be
   * delivered. One field, two jobs, on purpose: asking twice is how the second one
   * ends up empty.
   */
  urls: string[];
  /** True when it has none of its own and falls back to the tenant-wide list. */
  inherits_urls: boolean;
  metadata: { config?: Record<string, unknown> };
}

export type Env = "dev" | "live";

export interface EndUser {
  user_id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  status: string; // active | suspended | deleted
  created_at: string;
  /** Which front door this account was created at. Accounts do not cross apps. */
  app_id: string;
  /**
   * Which environment this account lives in — decided when they signed up, from
   * where they signed up: localhost is `dev`, everything else is `live`.
   *
   * `dev` accounts can always be deleted; a `live` one can only be deleted if it is
   * also marked `is_test`. Both halves are enforced by the server — see `deleteUser`.
   */
  env: Env;
  /** Marked by an operator as seeded or scratch data. Gates deletion — see `deleteUser`. */
  is_test: boolean;
  /** Free-form scratchpad. Any valid JSON object; nothing is enforced beyond that. */
  metadata: Record<string, unknown>;
  email_verified: boolean;
  phone_verified: boolean;
  identifiers: Identifier[];
}

/** Counts of ACCOUNTS, never of identifier rows — one person with two proved
 *  addresses is one verified user. */
export interface UserStats {
  total_users: number;
  active_users: number;
  suspended_users: number;
  with_email: number;
  with_phone: number;
  verified_email: number;
  verified_phone: number;
  unverified_users: number;
  active_last_30d: number;
  /** Reported ALONGSIDE `total_users`, not subtracted from it. */
  test_users: number;
  /** Accounts per app AND environment. Apps with none are absent, not zero. */
  by_app: Record<string, { dev: number; live: number }>;
  live_users: number;
  dev_users: number;
}

/** Per-account outcomes for a move or a copy. Never a bare success — an address
 *  the destination already holds cannot be taken, and that row needs naming. */
export interface AccountsOpResult {
  app_id: string;
  succeeded: number;
  failed: number;
  results: {
    user_id: string;
    /** `moved` | `copied`, or why not: `not_found` | `identifier_taken`. */
    outcome: string;
    detail: string | null;
    /** Copy only — the new account's id, which is never the source's. */
    new_user_id: string | null;
  }[];
}

/** What a dev wipe destroyed. The number is the reassurance. */
export interface DevResetResult {
  app_id: string;
  users_deleted: number;
}

export class Api {
  private readonly c: Client;

  /**
   * @param secretKey the tenant credential — it authenticates the TENANT.
   * @param actor who is holding it. Attribution, not authentication: the key already proved
   *   the tenant, and this says which person is using it. Without it every caller is a
   *   `viewer` the moment the tenant has any members at all, so the team screen refuses its
   *   own owner — and the admin log attributes every change to "unattributed (tenant key)",
   *   which is honest and useless.
   */
  constructor() {
    // NO CREDENTIAL. Every call goes to `/v1/bff/api/*` on this origin and the server attaches
    // the tenant key from the session it holds — see ui/console/src/session.ts and
    // app/module/bff/routes.py. The constructor used to take `secretKey` and `actor`; it takes
    // neither now, because there is nowhere in this bundle for either to live.
    //
    // The actor rides the session too. It was a header the browser set, which meant a browser
    // could claim to be anyone; the server recorded it at sign-in and stamps it here instead.
    this.c = new Client({ baseUrl: BFF_API_BASE, credentials: "same-origin" });
  }

  // ── reads ───────────────────────────────────────────────────────────────────────────
  tenant = () => this.c.get<Tenant>("/tenant");
  agents = () => this.c.get<Agent[]>("/agents");
  types = () => this.c.get<AgentType[]>("/agent/types");
  grants = (agentId?: string) =>
    this.c.get<Grant[]>("/agent/grants" + (agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""));
  mandates = (q: { subject_id?: string; subject_kind?: "agent" | "human" } = {}) => {
    const p = new URLSearchParams(q as Record<string, string>).toString();
    return this.c.get<Mandate[]>("/agent/mandates" + (p ? `?${p}` : ""));
  };
  usage = (period: "day" | "month" = "day") =>
    this.c.get<{ period: string; rows: UsageRow[] }>(`/agent/usage?period=${period}`);
  pending = (status?: string) =>
    this.c.get<{ pending: Pending[]; count: number }>(
      "/agent/pending?limit=200" + (status ? `&status=${status}` : ""),
    );
  decisions = (q: { agent_id?: string; hours?: number; limit?: number } = {}) => {
    const p = new URLSearchParams(
      Object.fromEntries(Object.entries(q).map(([k, v]) => [k, String(v)])),
    ).toString();
    return this.c.get<{ count: number; decisions: DecisionRow[]; is_record: boolean; window_note: string; record_url: string | null }>(
      "/agent/decisions" + (p ? `?${p}` : ""),
    );
  };
  decisionSummary = (hours = 24) =>
    this.c.get<DecisionSummary>(`/agent/decisions/summary?hours=${hours}`);
  connections = () => this.c.get<Connection[]>("/connections");
  mcpServers = () => this.c.get<{ name: string; url: string }[]>("/agent/mcp/servers");
  mcpPolicies = () =>
    this.c.get<{ server: string; tool: string; action_type: string }[]>("/agent/mcp/policies");
  detectors = () => this.c.get<Detector[]>("/agent/detectors");
  /**
   * The people who signed up through this tenant.
   *
   * `total` counts the FILTER, not the page — so a search that matches three of
   * four hundred says three, and the pager can be trusted against it.
   */
  apps = () => this.c.get<{ apps: TenantApp[] }>("/apps");

  users = (q: { limit?: number; offset?: number; q?: string; verified?: boolean; status?: string; is_test?: boolean; app_id?: string; env?: Env | "all" } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
    return this.c.get<{ users: EndUser[]; total: number; limit: number; offset: number }>(
      "/users" + (p.toString() ? `?${p}` : ""),
    );
  };
  userStats = () => this.c.get<UserStats>("/users/stats");

  /** Mark/unmark as test data, or replace the metadata scratchpad. Both reversible. */
  updateUser = (id: string, body: { is_test?: boolean; metadata?: Record<string, unknown>; name?: string }) =>
    this.c.patch<EndUser>(`/users/${encodeURIComponent(id)}`, body);

  /**
   * Erase an account and everything that identifies it. Irreversible.
   *
   * In `dev` this always works — the environment is scratch by definition. In
   * `live` the server refuses unless the account is marked `is_test`, and there is
   * no override this console could send even if it wanted to: DELETE has no such
   * flag. A genuine erasure request goes through a different route entirely, which
   * demands a recorded reason and is not reachable from here. See `delete_user` in
   * module/humans/admin.py.
   */
  deleteUser = (id: string) => this.c.del<void>(`/users/${encodeURIComponent(id)}?hard=true`);

  /**
   * Send accounts to another of this tenant's apps.
   *
   * `copy: false` MOVES them — same ids, so whatever your product stored against
   * them still finds the person, and the source app loses the account. Reversible
   * by moving them back.
   *
   * `copy: true` recreates them there instead. An account belongs to exactly one
   * app, so a copy is a genuinely new person: same address, NEW id, none of the
   * original's history. The original is untouched.
   *
   * Outcomes are per user because partial success is normal — an address the
   * destination already holds cannot be taken, and those rows need a human.
   */
  relocateAccounts = (
    fromAppId: string,
    b: { user_ids: string[]; to_app_id: string; copy: boolean; carry_verification?: boolean },
  ) => {
    const { copy, ...body } = b;
    return this.c.post<AccountsOpResult>(
      `/apps/${encodeURIComponent(fromAppId)}/accounts/${copy ? "copy" : "move"}`,
      body,
    );
  };

  /** Erase every dev account in an app. Irreversible, and the point of having a dev env. */
  resetDev = (appId: string) =>
    this.c.post<DevResetResult>(`/apps/${encodeURIComponent(appId)}/dev/reset`, {});

  /**
   * Add a front door. The URL is the whole question — the name and the internal
   * handle derive from it, and it doubles as the app's origin allow-list.
   *
   * It starts isolated (its own account space) and has both environments
   * immediately: localhost signs in to its dev accounts, everywhere else to live.
   */
  createApp = (b: { urls?: string[]; name?: string }) => this.c.post<TenantApp>("/apps", b);

  /**
   * Rename, suspend or reactivate. `slug` is deliberately not patchable: it is what
   * logs, tokens and support conversations name an app by, so renaming one would
   * rewrite the meaning of every past mention.
   */
  updateApp = (appId: string, b: { name?: string; status?: string; urls?: string[] }) =>
    this.c.patch<TenantApp>(`/apps/${encodeURIComponent(appId)}`, b);

  /**
   * Close a front door. The server refuses while any account is still behind it —
   * deleting would leave those people unreachable, holding their addresses in a
   * scope nothing can sign in to. Clear them first, or suspend instead.
   */
  deleteApp = (appId: string) => this.c.del<void>(`/apps/${encodeURIComponent(appId)}`);

  members = () => this.c.get<Member[]>("/team/members");
  audit = () => this.c.get<{ count: number; events: Record<string, unknown>[]; note: string }>("/team/audit");
  reasonCodes = () => this.c.get<ReasonCodes>("/agent/reason-codes");
  wouldHaveBlocked = (agentId?: string) =>
    this.c.get<ShadowReport>(
      "/agent/would-have-blocked" + (agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""),
    );
  mandateProposal = (agentId: string) =>
    this.c.get<MandateProposal>(`/agent/${encodeURIComponent(agentId)}/mandate-proposal`);
  counters = (agentId: string) =>
    this.c.get<{ agent_id: string; decisions_authorized: number; gate: string }>(
      `/agents/${encodeURIComponent(agentId)}/counters`,
    );

  /** The exact canonical bytes to sign, per outcome.
   *
   *  `to_sign` is only populated when the approver mandate AND both reason fields are given —
   *  and `reason_code` must match byte-for-byte what is later sent to `resolve()`, or the
   *  signature covers a different body and verification fails. That footgun is why this takes
   *  the whole query at once rather than letting a caller assemble it. */
  pendingDetail = (
    pendingId: string,
    q: { approver_mandate_id: string; reason_class: string; reason_code: string },
  ) =>
    this.c.get<Pending & { to_sign: Record<string, string> }>(
      `/agent/pending/${encodeURIComponent(pendingId)}?` + new URLSearchParams(q).toString(),
    );

  // ── writes ──────────────────────────────────────────────────────────────────────────
  resolve = (b: {
    pending_id: string;
    resolution: "approve" | "deny";
    reason_class: string;
    reason_code: string;
    approver_mandate_id: string;
    signature: string;
  }) =>
    this.c.post<{ pending_id: string; resolution: string; verified: boolean; approver_did: string }>(
      "/agent/resolve",
      b,
    );

  registerAgent = (b: { public_key: string; name?: string; kind?: string; principal_did?: string }) =>
    this.c.post<{ agent_id: string; agent_did: string }>("/agent/register", b);
  revokeAgent = (id: string) => this.c.post<unknown>(`/agent/${encodeURIComponent(id)}/revoke`);

  issueMandate = (b: {
    subject_id: string;
    subject_kind?: "agent" | "human";
    subject_did?: string;
    budget: string;
    currency?: string;
    allowed_actions: string[];
    escalate_over?: string | null;
    enforcement?: "deny" | "warn" | "dryrun";
  }) => this.c.post<Mandate>("/agent/mandate", b);
  revokeMandate = (id: string) => this.c.post<Mandate>(`/agent/mandate/${encodeURIComponent(id)}/revoke`);

  setUsagePolicy = (b: { agent_id: string; limit_units: number; resource?: string; period?: string }) =>
    this.c.post<unknown>("/agent/usage/policy", b);
  /** Removing the row IS the operation — "no policy" is the ledger's own encoding of
   *  metered-only. There is no `limit = ∞` to set. */
  clearUsagePolicy = (agentId: string, resource = "inference_usd") =>
    this.c.del<void>(`/agent/usage/policy?agent_id=${encodeURIComponent(agentId)}&resource=${resource}`);

  rotateSecret = () => this.c.post<{ tenant_id: string; secret_key: string }>("/tenant/rotate-secret");

  /** The on-ramp the MCP firewall was missing: a passport lives 300 seconds and cannot sit in a
   *  static `mcpServers` block a client reads once at startup. Without this the firewall is
   *  configurable and unreachable from a real client.
   *
   *  Takes a MANDATE, not a server. The mandate rides *inside* the signed token, which is what
   *  makes the client config exactly one header — an MCP client holds static headers in a JSON
   *  file and cannot compute a per-request mandate header. */
  mintMcpCredential = (b: { agent_id: string; mandate_id: string; ttl_days?: number }) =>
    this.c.post<{
      credential: string;
      token_type: string;
      agent_id: string;
      mandate_id: string;
      audience: string;
      expires_at: string;
      /** The lines an operator actually pastes. Handing them the bare token means they get to
       *  guess the header name. */
      config_headers: Record<string, string>;
    }>("/agent/mcp-credential", b);

  addMember = (b: { email: string; role: string }) => this.c.post<Member>("/team/members", b);
  setRole = (id: string, role: string) =>
    this.c.post<Member>(`/team/members/${encodeURIComponent(id)}/role`, { role });
  registerDetector = (b: { name: string; public_key: string }) =>
    this.c.post<Detector>("/agent/detectors", b);
  deactivateDetector = (id: string) =>
    this.c.post<unknown>(`/agent/detectors/${encodeURIComponent(id)}/deactivate`);
  declareConnection = (provider: string) =>
    this.c.post<Connection>("/connections/declare", { provider });
}

/** µUSD → dollars. The ledger stores integers; a fractional micro-dollar is not a thing. */
export const microToUsd = (u: number) => u / 1_000_000;
export const usdToMicro = (d: number) => Math.round(d * 1_000_000);
